import { ChangeDetectionStrategy, Component, inject, signal, Signal } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectChange, MatSelectModule } from '@angular/material/select'
import { MatSortModule, Sort } from '@angular/material/sort'
import { MatTableModule } from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import { Router } from '@angular/router'
import { HeaderBarComponent } from '@components/header-bar/header-bar.component'
import { DialogCloneRepositoryComponent } from '@components/integration-platform/dialog-clone-repository/dialog-clone-repository.component'
import { DialogDeleteIntegrationComponent } from '@components/integration-platform/dialog-delete-integration/dialog-delete-integration.component'
import { DialogLayerIntegrationComponent } from '@components/integration-platform/dialog-layer-integration/dialog-layer-integration.component'
import { DialogUpdateRepositoryComponent } from '@components/integration-platform/dialog-update-repository/dialog-update-repository.component'
import { IntegrationSidebarComponent } from '@components/integration-platform/integration-sidebar/integration-sidebar.component'
import { ADMIN_GEOM_TABLE_NAMES } from '@core/constants/constant.data'
import { LayerCloned } from '@core/models/layers.model'
import { LayersClonedService } from '@core/services/layers-cloned.service'

// Maps a mat-sort-header column id (inferred from its matColumnDef) to the LayerCloned field it should sort by.
const SORTABLE_COLUMNS: Partial<Record<string, keyof LayerCloned>> = {
  layerName: 'layerName',
  repoName: 'repo',
}

@Component({
  selector: 'app-integration-layers',
  templateUrl: './integration-layers.component.html',
  styleUrls: ['./integration-layers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    HeaderBarComponent,
    IntegrationSidebarComponent,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSortModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatTooltip,
  ],
})
export class IntegrationLayersComponent {
  layersClonedService = inject(LayersClonedService)
  private router = inject(Router)
  displayedColumns: string[] = [
    'layerError',
    'layerName',
    'layerType',
    'repoName',
    'integration',
    'actions',
  ]
  layersCloned$: Signal<LayerCloned[]> = this.layersClonedService.layersCloned$
  readonly dialog = inject(MatDialog)
  isLayersClonedLoading$ = this.layersClonedService.isLayersClonedLoading$
  filterValue$ = signal<string>('')
  layerTypes = new FormControl('')
  layersTypesOptions = this.layersClonedService.getLayerIntegrationTypes()
  private sortState: Sort = { active: '', direction: '' }

  trackBy(_index: number, item: LayerCloned): string {
    return `${item.repo}-${item.layerName}`
  }

  completeDeleteIntegration(repoName: string, layerName: string) {
    const deleteDialogRef = this.dialog.open(DialogDeleteIntegrationComponent, {
      data: { repoName: repoName, layerName: layerName },
    })
    deleteDialogRef.afterClosed().subscribe((result) => this.refreshLayers(result))
  }

  completeRepositoryClone() {
    const cloneDialogRef = this.dialog.open(DialogCloneRepositoryComponent, {
      width: '500px',
      height: 'auto',
      maxWidth: '90vw',
    })
    cloneDialogRef.afterClosed().subscribe((result) => this.refreshLayers(result))
  }

  async refreshLayersCloned() {
    await this.layersClonedService.refreshLayersCloned()
    this._refreshDisplayedLayers()
  }

  completeRepositoryUpdate(repoName: string) {
    const cloneDialogRef = this.dialog.open(DialogUpdateRepositoryComponent, {
      width: '500px',
      height: 'auto',
      maxWidth: '90vw',
      data: { repoName: repoName },
    })
    cloneDialogRef.afterClosed().subscribe((result) => this.refreshLayers(result))
  }

  completeLayerIntegration(repoName: string, layerType: string) {
    const cloneDialogRef = this.dialog.open(DialogLayerIntegrationComponent, {
      data: {
        repoName: repoName,
        layerType: layerType,
        adminGeomTableNames: ADMIN_GEOM_TABLE_NAMES,
      },
    })
    cloneDialogRef.afterClosed().subscribe((result) => this.refreshLayers(result))
  }

  private refreshLayers(result: boolean | undefined) {
    if (result) {
      this.refreshLayersCloned()
    }
  }

  openIntegrationLayerDetailed(layerCloned: LayerCloned) {
    this.router.navigate(['/integration/layers', layerCloned.layerName, 'detailed'], {
      state: { layerCloned },
    })
  }

  applyFilter(event: Event) {
    const filter = (event.target as HTMLInputElement).value
    this.filterValue$.set(filter)
    this._refreshDisplayedLayers()
  }

  onLayerTypeChange(event: MatSelectChange) {
    this.layerTypes.setValue(event.value)
    this._refreshDisplayedLayers()
  }

  onSortChange(sort: Sort) {
    this.sortState = sort
    this._refreshDisplayedLayers()
  }

  private _refreshDisplayedLayers() {
    const selectedTypes = this.layerTypes.value || []
    const filtered = this.layersClonedService.filterLayersCloned(
      this.filterValue$(),
      Array.isArray(selectedTypes) ? selectedTypes : [selectedTypes],
    )()
    this.layersCloned$ = signal(this._sortLayers(filtered))
  }

  private _sortLayers(layers: LayerCloned[]): LayerCloned[] {
    const { active, direction } = this.sortState
    const sortKey = SORTABLE_COLUMNS[active]
    if (!sortKey || !direction) {
      return layers
    }

    const sorted = [...layers].sort((a, b) =>
      (a[sortKey] as string).localeCompare(b[sortKey] as string),
    )
    return direction === 'asc' ? sorted : sorted.reverse()
  }
}
